import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LayoutDashboard, LogOut, Package, Palette, PenLine, ShoppingBag, Sparkles, Store, Upload, Users } from "lucide-react";
import { Toaster } from "sonner";
import { api } from "../../lib/api";
import Seo from "../../components/Seo";
import Dashboard from "./Dashboard";
import Orders from "./Orders";
import CustomOrders from "./CustomOrders";
import Products from "./Products";
import Inquiries from "./Inquiries";
import Content from "./Content";
import MediaLibrary from "./MediaLibrary";

export const SECTIONS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "custom-orders", label: "Custom Orders", icon: Sparkles },
  { key: "products", label: "Products", icon: Package },
  { key: "inquiries", label: "Inquiries", icon: Users },
  { key: "content", label: "Content & Settings", icon: PenLine },
  { key: "media", label: "Media", icon: Upload },
];

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      onSuccess(await api("/auth/login", { method: "POST", body: { email, password } }));
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-candy-mist flex items-center justify-center p-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="bg-white rounded-cloud shadow-card p-8 w-full max-w-sm">
        <img src="/assets/logo.jpg" alt="Hooked by Rachel" className="h-14 mx-auto" />
        <h1 className="font-display font-semibold text-2xl text-center mt-4" data-testid="admin-login-heading">Admin Login</h1>
        <p className="text-muted text-sm text-center font-semibold mt-1">For the store owner only.</p>
        <label className="block mt-6">
          <span className="text-sm font-bold text-ink/80">Email</span>
          <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-2xl bg-candy-mist border border-candy-soft focus:border-candy outline-none font-semibold" />
        </label>
        <label className="block mt-3">
          <span className="text-sm font-bold text-ink/80">Password</span>
          <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-2xl bg-candy-mist border border-candy-soft focus:border-candy outline-none font-semibold" />
        </label>
        {error && <p className="text-red-500 text-sm font-bold mt-3" data-testid="admin-login-error">{error}</p>}
        <button disabled={busy} data-testid="admin-login-submit" className="mt-5 w-full py-3.5 rounded-full bg-candy hover:bg-candy-deep disabled:opacity-60 text-white font-bold shadow-pink transition-colors duration-200">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <Link to="/" data-testid="admin-back-to-store" className="block text-center text-sm font-bold text-muted hover:text-candy-deep mt-4 transition-colors duration-200">← Back to the store</Link>
      </form>
    </div>
  );
}

export default function AdminApp() {
  const [user, setUser] = useState(undefined);
  const { section = "dashboard" } = useParams();
  const navigate = useNavigate();

  useEffect(() => { api("/auth/me").then(setUser).catch(() => setUser(null)); }, []);

  if (user === undefined) return <div className="min-h-screen bg-candy-mist" />;
  if (user === null) return <><Login onSuccess={setUser} /><Toaster richColors position="top-center" /></>;

  const logout = async () => {
    try { await api("/auth/logout", { method: "POST" }); } catch { /* ignore */ }
    setUser(null);
  };

  const views = {
    dashboard: <Dashboard />,
    orders: <Orders />,
    "custom-orders": <CustomOrders />,
    products: <Products />,
    inquiries: <Inquiries />,
    content: <Content />,
    media: <MediaLibrary />,
  };

  return (
    <div className="min-h-screen bg-candy-mist flex" data-testid="admin-app">
      <Seo title="Admin" noindex />
      <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-candy-soft/70 p-4 sticky top-0 h-screen">
        <img src="/assets/logo.jpg" alt="Hooked by Rachel" className="h-12 mx-auto" />
        <nav className="mt-8 flex flex-col gap-1" aria-label="Admin">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              data-testid={`admin-nav-${s.key}`}
              onClick={() => navigate(`/admin/${s.key}`)}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-2xl font-bold text-sm transition-colors duration-200 ${section === s.key ? "bg-candy-glow text-candy-deep" : "text-ink/70 hover:bg-candy-soft"}`}
            >
              <s.icon className="w-4 h-4" /> {s.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto pt-4 border-t border-candy-soft/70 space-y-1">
          <Link to="/" data-testid="admin-view-store" className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl font-bold text-sm text-ink/70 hover:bg-candy-soft transition-colors duration-200">
            <Store className="w-4 h-4" /> View store
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-2xl font-bold text-sm text-ink/70 hover:bg-candy-soft transition-colors duration-200">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="lg:hidden bg-white border-b border-candy-soft/70 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <img src="/assets/logo.jpg" alt="Hooked by Rachel" className="h-9" />
          <div className="flex gap-1 overflow-x-auto max-w-[70%]">
            {SECTIONS.map((s) => (
              <button key={s.key} data-testid={`admin-mobile-nav-${s.key}`} onClick={() => navigate(`/admin/${s.key}`)} className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${section === s.key ? "bg-candy-glow text-candy-deep" : "bg-candy-mist text-ink/60"}`}>{s.label}</button>
            ))}
          </div>
        </header>
        <main className="p-4 sm:p-8 max-w-6xl mx-auto">{views[section] || views.dashboard}</main>
      </div>
      <Toaster richColors position="top-center" />
    </div>
  );
}
